import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {useEffect} from 'react';
import {Transition} from 'react-transition-group';
import {handleEnterAnimation, handleExitAnimation} from '../../animations/timelines/modal-transitions';
import UxHelpers from '../../helpers/ux-helpers';
import './styles.css';

const Popup = ({
  title,
  actions,
  bodyRender,
  buttonsRender,
  compact,
  popupStyles,
  popupHeaderStyles,
  popupTitleStyles,
  popupInnerStyles,
  modeless = true,
  dismissWhenClose = true,
  hasCloseButton = true,
  className,
}) => {
  const styles = {
    ...popupStyles,
  };

  const close = () => {
    if (dismissWhenClose) {
      actions.dismiss();
    } else {
      actions.cancel();
    }
    UxHelpers.overflowLock('unlock');
  };

  const onEsc = e => {
    if (e.key === 'Escape' && hasCloseButton) {
      close();
    }
  };

  const onClickMask = () => {
    if (modeless) {
      close();
      UxHelpers.overflowLock('unlock');
    }
  };

  const listenerLocationChange = () => {
    UxHelpers.overflowLock('unlock');
    actions.dismiss();
  };

  useEffect(() => {
    UxHelpers.overflowLock('lock');
    window.addEventListener('keydown', onEsc);
    window.addEventListener('popstate', listenerLocationChange);
    if ('activeElement' in document) {
      document.activeElement.blur();
    }
    return () => {
      window.removeEventListener('keydown', onEsc);
      window.removeEventListener('popstate', listenerLocationChange);
      UxHelpers.overflowLock('unlock');
    };
  }, []);

  return (
    <div className={classNames('popup__overlay', className)}>
      <div className="popup__mask" onClick={onClickMask} />
      <Transition
        timeout={50}
        in={true}
        appear={true}
        mountOnEnter={true}
        unmountOnExit={true}
        onEnter={node => handleEnterAnimation(node)}
        onExit={node => handleExitAnimation(node)}
      >
        <div
          className={!compact ? 'popup' : 'popup popup--compact'}
          style={styles}
        >
          <div
            className={
              title
                ? 'popup__header popup__header--with-title'
                : 'popup__header'
            }
            style={popupHeaderStyles}
          >
            {title && (
              <h3
                className={
                  !compact
                    ? 'popup__title'
                    : 'popup__title popup__title--compact'
                }
                style={popupTitleStyles}
              >
                {title}
              </h3>
            )}
            {hasCloseButton && (
              <span className="popup__close" onClick={close} />
            )}
          </div>

          <div
            className={
              !compact ? 'popup__inner' : 'popup__inner popup__inner--compact'
            }
            style={popupInnerStyles}
          >
            <div
              className={
                !compact
                  ? 'popup__content'
                  : 'popup__content popup__content--compact'
              }
            >
              {bodyRender}
            </div>
            {buttonsRender && (
              <div className="popup__buttons">{buttonsRender}</div>
            )}
          </div>
        </div>
      </Transition>
    </div>
  );
};

Popup.propTypes = {
  actions: PropTypes.object,
  buttonsRender: PropTypes.object,
  bodyRender: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
  popupStyles: PropTypes.object,
  modeless: PropTypes.bool,
  hasCloseButton: PropTypes.bool,
  dismissWhenClose: PropTypes.bool,
  popupInnerStyles: PropTypes.object,
  popupHeaderStyles: PropTypes.object,
  popupTitleStyles: PropTypes.object,
};

export default Popup;
